<template>
  <div class="taxes-selector">
    <div class="row">
      <div class="form-wrapper col-md-12 p-2">
        <el-select
          v-model="taxModel"
          @change="taxChanged"
          autocomplete="new-password"
          :placeholder="$t('TAXES.SELECT')"
          :filterable="filterable"
          :multiple="multiple"
          :disabled="disabled"
          remote
          :remote-method="getList"
          :loading="loading"
          :allow-create="allowCreate"
          class="w-100"
        >
          <el-option v-if="showAll" :value="null" :label="$t('COMMON.ALL')">
          </el-option>
          <el-option v-if="allowNone" :value="null" :label="$t('COMMON.NONE')">
          </el-option>
          <el-option
            v-for="tax in taxes"
            :key="tax.id"
            :value="tax.id"
            :label="tax.name"
          >
            <template #default>
              <span class="taxe-name">{{ tax.name }}</span>
              <em class="text-gray" v-if="tax.calculation_type == typePourcentage"
                > - {{ tax.value }} %</em
              >
              <em class="text-gray" v-else-if="tax.calculation_type == typeMontantFixe"> - {{
                $formatCurrency(tax.value)
              }}</em>
              <em class="text-gray" v-else> - {{ $t("TAXES." + tax.calculation_type) }}</em>
            </template>
          </el-option>
          <el-option
            disabled
            v-if="taxes.length == 0"
            :value="null"
            :label="$t('TAXES.NO_TAXES_FOUND')"
          >
          </el-option>
        </el-select>
      </div>
      <div v-if="withFilters" class="col-md-12">
        <!-- Liste des taxes sélectionnées -->
        <div v-if="selectedTaxes.length > 0" class="selected-taxes mt-4">
          <h5 class="text-uppercase text-center">{{ $t("TAXES.SELECTED_TAXES") }}</h5>
          <div class="selected-taxes-list">
            <div class="row">
              <div class="sales col-md-12">
                <draggable
                  v-model="selectedTaxes"
                  class="draggable-list"
                  :group="'taxes'"
                  @change="emitTaxChangedEvent"
                >
                  <div
                    v-for="(tax, index) in selectedTaxes"
                    :key="index"
                    class="taxe-item-p"
                  >
                    <div class="taxe-item d-flex justify-content-between align-items-center mb-2 border rounded">
                      <div class="taxe-name float-left">
                        <span class="btn-inner--text mr-2">
                          <i class="fa fa-bars"></i>
                        </span>
                        <div class="full-name float-right">
                          <span class="taxe-name">{{ tax.name }}</span>
                          <em class="text-gray" v-if="tax.calculation_type == typePourcentage"
                            > - {{ tax.value }} %</em
                          >
                          <em class="text-gray" v-else-if="tax.calculation_type == typeMontantFixe"> - {{
                            $formatCurrency(tax.value)
                          }}</em>
                          <em class="text-gray" v-else> - {{ $t("TAXES." + tax.calculation_type) }}</em>
                        </div>
                      </div>
                      <span
                        @click="removeTax(tax.id)"
                        class="cursor-pointer btn-inner--text text-danger"
                      >
                        <i class="fa fa-trash"></i>
                      </span>
                    </div>
                  </div>
                </draggable>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Option, Select } from "element-ui";
import draggable from "vuedraggable";
import _ from "lodash";
import {
  TYPE_POURCENTAGE,
  TYPE_MONTANT_FIXE,
  TAXE_TRANSACTIONS_TYPES,
  TAXE_TYPES,
  CALCULATION_BASES,
} from "@/constants/taxes";

export default {
  name: "tax-selector",

  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    draggable,
  },

  props: {
    withFilters: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    allowCreate: {
      type: Boolean,
      default: false,
    },
    showAll: {
      type: Boolean,
      default: true,
    },
    allowNone: {
      type: Boolean,
      default: false,
    },
    filterable: {
      type: Boolean,
      default: true,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    tax: {
      type: [String, Array],
      default: null,
    },
    selectedTransactionType: {
      type: String,
      default: null,
    },
    selectedType: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      selectedTaxeTransactionType: null,
      taxeTransactions: TAXE_TRANSACTIONS_TYPES,
      calculationBases: CALCULATION_BASES,
      typePourcentage: TYPE_POURCENTAGE,
      typeMontantFixe: TYPE_MONTANT_FIXE,
      taxeTypes: TAXE_TYPES,
      sort: "-created_at",
      taxModel: this.tax,
      taxes: [],
      selectedTaxes: [],
      loading: false,
      query: "",
    };
  },

  created() {
    this.selectedTaxeTransactionType = this.selectedTransactionType;
    this.getList(null, this.tax);
  },

  methods: {
    getListDebounced: _.debounce(function () {
      this.getList();
    }, 300),

    async getList(query = null, ids = null) {
      try {
        this.loading = true;
        let params = {
          sort: this.sort,
          filter: {
            ...(query ? { search: query } : {}),
            ...(ids ? { id: ids } : {}),
          },
          page: {
            number: 1,
            size: 10,
          },
        };

        if (this.selectedType) {
          params.filter.calculation_type = this.selectedType;
        }

        if (this.selectedTaxeTransactionType) {
          params.filter.tax_type = this.selectedTaxeTransactionType;
        }

        await this.$store.dispatch("taxes/list", params);
        const taxesArr = await this.$store.getters["taxes/list"];
        this.taxes = taxesArr;
      } catch (error) {
        console.log(error);
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      } finally {
        this.loading = false;
      }
    },

    taxChanged(selectedTaxIds) {
      const currentSelectedTaxes = [...this.selectedTaxes];

      const fromCurrentSelected = currentSelectedTaxes.filter((tax) =>
        selectedTaxIds.includes(tax.id)
      );

      const fromAllTaxes = this.taxes.filter(
        (tax) =>
          selectedTaxIds.includes(tax.id) &&
          !currentSelectedTaxes.some((c) => c.id === tax.id)
      );

      const newSelectedTaxes = [...fromCurrentSelected, ...fromAllTaxes];
      this.selectedTaxes = newSelectedTaxes;
      this.$emit("taxChanged", selectedTaxIds);
    },

    removeTax(taxId) {
      this.selectedTaxes = this.selectedTaxes.filter(
        (tax) => tax.id !== taxId
      );

      this.taxModel = this.taxModel.filter((id) => id !== taxId);

      this.emitTaxChangedEvent();
    },

    emitTaxChangedEvent() {
      const selectedTaxIds = this.selectedTaxes.map((tax) => tax.id);
      this.$emit("taxChanged", selectedTaxIds);
    },
  },

  watch: {
    selectedTaxeTransactionType: {
      handler: "getListDebounced",
      immediate: true,
    },
    tax(newTax) {
      if (newTax) {
        this.taxModel = newTax;
        if (newTax !== this.taxModel) {
          this.getList(null, newTax);
        }
      } else {
        this.taxModel = null;
      }
    },
  },
};
</script>

<style>
.draggable-list {
  list-style: none;
  padding: 0;
}
.taxe-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f9f9f9;
  padding: 0px 10px;
}
.cursor-pointer {
  cursor: pointer;
}
.taxe-type {
  font-size: 11px;
}
</style>
